/* Backend simulado: responde às mesmas rotas de /api/v1/wl, no mesmo formato. */

import * as wl from './agregacoes';
import { DEMO_CATALOGO, FATOR_DEMO, FATOR_DESTINO_DEMO, MATERIAIS, type MaterialDemo } from './catalogo';
import { agregar, apagarGrafico, ErroGrafico, GRAFICOS_SALVOS, salvarGrafico } from './graficos';
import { ENVIOS, LANCAMENTOS, novaLinha, proximoId, registrarEnvio, type LinhaDemo } from './dados';
import { opcaoDeUnidade } from '../core/analisePlanilha';
import { isoDe, isoDeTexto, parseNum, unidadeTela } from '../core/formato';
import { casarReferencia } from '../core/referencias';
import type { ItemLista, Obra, ResultadoImportacao, ResultadoLancamento, SimCatalogo, SimResultado, TipoDocumento } from '../tipos/api';

type Params = Record<string, string | number | undefined | null>;

export class ErroDemo extends Error {
  constructor(mensagem: string, readonly status = 400, readonly payload?: unknown) {
    super(mensagem);
  }
}

function filtrar(p: Params, usarEscopo = true): LinhaDemo[] {
  const ano = Number(p.ano || 0);
  return LANCAMENTOS.filter((r) =>
    (!ano || Number(r.data.slice(0, 4)) === ano) &&
    (!p.id_unidade || r.id_unidade === p.id_unidade) &&
    (!usarEscopo || !p.escopo || String(r.escopo) === String(p.escopo)),
  ).sort((a, b) => b.data.localeCompare(a.data));
}

/** Conversão para a unidade do fator, como em escopo_3/bens_servicos_comprados/calculator.py. */
export function converterQuantidade(qtd: number, de: string | undefined, mat: Pick<MaterialDemo, 'nome' | 'unidade' | 'densidade'>): number {
  const para = mat.unidade;
  const d = ({ 'm³': 'm3', 'm²': 'm2' } as Record<string, string>)[de || ''] || de || para;
  const MASSA: Record<string, number> = { kg: 1, t: 1000 };
  const VOL: Record<string, number> = { L: 0.001, m3: 1 };
  if (d === para) return qtd;
  if (MASSA[d] && MASSA[para]) return (qtd * MASSA[d]) / MASSA[para];
  if (VOL[d] && VOL[para]) return (qtd * VOL[d]) / VOL[para];
  if (mat.densidade && MASSA[d] && VOL[para]) return (qtd * MASSA[d]) / mat.densidade / VOL[para];
  if (mat.densidade && VOL[d] && MASSA[para]) return (qtd * VOL[d] * mat.densidade) / MASSA[para];
  throw new ErroDemo(`Não é possível converter ${de} em ${unidadeTela(para)} para ${mat.nome}. Informe a quantidade em ${unidadeTela(para)}.`);
}

/** ID, nome ou descrição parecida, como services._resolver_item (app/core/referencias.py). */
function resolverItem(valor: unknown, itens: ItemLista[], rotulo: string): ItemLista['id'] {
  const r = casarReferencia(valor, itens);
  if (r.ok) return r.item.id;
  if (!r.sugestoes.length) throw new ErroDemo(`${rotulo} não encontrado no catálogo: ${valor}`);
  throw new ErroDemo(`${rotulo} não identificado pela descrição '${String(valor).trim()}'. Mais parecidos: ${r.sugestoes.map((i) => `${i.id} - ${i.nome}`).join('; ')}`);
}

/** Campos do documento (descrição, obra…): o simulador não pede, só a data. */
const CAMPOS_DOCUMENTO_DEMO = new Set(['descricao', 'id_unidade', 'cnpj_fornecedor', 'link_referencia']);

/** Valida e calcula (sem gravar). Com documento = false, para o simulador. */
function calcularDemo(dados: Record<string, unknown>, comDocumento = true) {
  const cat = DEMO_CATALOGO.categorias.find((c) => c.slug === dados.categoria);
  if (!cat) throw new ErroDemo(`Categoria não reconhecida: ${dados.categoria}`);
  const v: Record<string, unknown> = {};
  const faltam: string[] = [];
  cat.campos.filter((c) => comDocumento || !CAMPOS_DOCUMENTO_DEMO.has(c.nome)).forEach((c) => {
    let bruto = dados[c.nome];
    if (bruto === undefined || bruto === null || String(bruto).trim() === '') {
      if (c.padrao !== null && c.padrao !== undefined) bruto = c.padrao;
      else {
        if (c.obrigatorio) faltam.push(c.rotulo);
        return;
      }
    }
    if (c.tipo === 'numero') {
      const n = parseNum(bruto);
      if (!Number.isFinite(n)) throw new ErroDemo(`${c.rotulo}: número inválido (${bruto}).`);
      v[c.nome] = n;
    } else if (c.tipo === 'data') {
      v.data = bruto instanceof Date ? isoDe(bruto) : isoDeTexto(bruto);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(v.data))) throw new ErroDemo(`Data inválida: ${bruto}.`);
    } else if (c.tipo === 'obra') v.id_unidade = resolverItem(bruto, DEMO_CATALOGO.obras, 'Obra');
    else if (c.tipo.startsWith('lista:')) v[c.nome] = resolverItem(bruto, DEMO_CATALOGO.listas[c.tipo.slice(6)], c.rotulo);
    else if (c.tipo === 'opcoes') {
      const o = opcaoDeUnidade(bruto, c.opcoes);
      if (!o) throw new ErroDemo(`${c.rotulo}: use ${c.opcoes.join(', ')}.`);
      v[c.nome] = o;
    } else v[c.nome] = String(bruto).trim();
  });
  if (faltam.length) throw new ErroDemo('Campos obrigatórios ausentes: ' + faltam.join(', '));
  const qtd = Number(v.consumo ?? v.consumo_mensal ?? v.existentes_recarga ?? v.quantidade ?? v.volume_m3 ?? 0);
  if (qtd <= 0) throw new ErroDemo('Informe uma quantidade maior que zero.');

  let total: number;
  let extra = {};
  if (cat.slug === 'bens-servicos-comprados') {
    const mat = MATERIAIS.find((m) => m.id === v.id_material) as MaterialDemo;
    total = (converterQuantidade(qtd, v.unidade as string | undefined, mat) * mat.fe) / 1000;
    extra = { id_material: mat.id, quantidade: (total * 1000) / mat.fe, valor_total: Number(v.valor_total || 0) };
  } else if (cat.slug === 'residuos-ipcc') {
    total = (v.unidade === 'kg' ? qtd / 1000 : qtd) * (FATOR_DESTINO_DEMO[Number(v.id_destino)] ?? 0.5);
  } else if (cat.slug === 'efluentes-ipcc-dbo') {
    // Mesma fórmula de efluentes_ipcc_dbo/calculator.py: MCF × B0 × DBO × volume / 1000 × GWP 28.
    const mcf = DEMO_CATALOGO.listas.tratamentos_efluentes.find((t) => t.id === v.id_tratamento)?.mcf ?? 0.5;
    total = (mcf * Number(v.maximo_ch4 ?? 0.6) * Number(v.dbo_kg_m3) * qtd / 1000) * 28;
  } else {
    total = qtd * FATOR_DEMO[cat.slug] * (v.unidade === 't' ? 1000 : 1);
  }
  return { cat, v, total, extra };
}

/** Validação equivalente a services.validar_lancamento + cálculo aproximado, gravando na sessão. */
export function lancar(dados: Record<string, unknown>): ResultadoLancamento {
  const { cat, v, total, extra } = calcularDemo(dados);
  const l = novaLinha({
    id: proximoId(), data: String(v.data), descricao: String(v.descricao), cnpj_fornecedor: String(v.cnpj_fornecedor || ''),
    id_categoria: cat.id_categoria, id_unidade: String(v.id_unidade), total, ...extra,
  });
  LANCAMENTOS.push(l);
  return {
    documento: { id: l.id, data: l.data, id_unidade_negocio: l.id_unidade, id_categoria: l.id_categoria },
    emissao: { emissao_co2: l.co2, emissao_ch4: l.ch4, emissao_n2o: l.n2o, emissao_total: l.total, emissao_biogenica: l.biogenica },
    categoria: { slug: cat.slug, nome: cat.nome, escopo: cat.escopo },
  };
}

/**
 * Importação de planilha no modo demonstração: linha a linha, parando na primeira com erro,
 * e registrando o envio em "Meus envios" (como POST /wl/lancamentos/excel faz no backend).
 */
export function importarPlanilhaDemo(
  slug: string,
  arquivo: string,
  linhas: { dados: Record<string, unknown>; n: number }[],
): ResultadoImportacao {
  let inseridas = 0;
  for (const r of linhas) {
    try {
      lancar({ categoria: slug, ...r.dados });
      inseridas++;
    } catch (e) {
      const motivo = (e as Error).message;
      registrarEnvio({
        tipo: 'planilha', origem: arquivo, categoria: slug, status: inseridas ? 'parcial' : 'erro',
        mensagem: `${inseridas} linha(s) gravada(s); parou na linha ${r.n}: ${motivo}`,
      });
      const mensagem = 'Falha ao importar uma linha do Excel.';
      throw new ErroDemo(mensagem, 422, {
        error: { message: mensagem, details: { linha_excel: r.n, linhas_inseridas: inseridas, importacao_parcial: inseridas > 0, detalhes: { status: 'error', error: motivo } } },
      });
    }
  }
  registrarEnvio({ tipo: 'planilha', origem: arquivo, categoria: slug, status: 'importado', mensagem: `${inseridas} lançamento(s) gravado(s)` });
  return { linhas_lidas: linhas.length, linhas_inseridas: inseridas };
}

/** Mesmas regras de services.validar_obra_e_tipo: obra obrigatória, tipo opcional. */
function obraETipo(obra: unknown, tipo: unknown): { obra: Obra; tipo_documento: TipoDocumento | null } {
  if (!String(obra ?? '').trim()) throw new ErroDemo('Informe a obra a que os documentos se referem.');
  const id = resolverItem(obra, DEMO_CATALOGO.obras, 'Obra');
  const t = String(tipo ?? '').trim();
  if (t && !DEMO_CATALOGO.tipos_documento.some((x) => x.id === t)) throw new ErroDemo(`Tipo de documento inválido: ${t}.`);
  return { obra: DEMO_CATALOGO.obras.find((o) => o.id === id) as Obra, tipo_documento: (t || null) as TipoDocumento | null };
}

const camposObra = (o: ReturnType<typeof obraETipo>) => ({ id_unidade: o.obra.id, obra: o.obra.nome, tipo_documento: o.tipo_documento });

/** Erros de validação dos gráficos viram erro 400, como no backend. */
function comoErroDemo<T>(fn: () => T): T {
  try {
    return fn();
  } catch (e) {
    if (e instanceof ErroGrafico) throw new ErroDemo(e.message);
    throw e;
  }
}

/** Catálogo do simulador no modo demonstração: as categorias do catálogo, sem os campos do documento. */
export function simuladorDemo(): SimCatalogo {
  return {
    versoes: ['v2025.0.1'],
    versao_padrao: 'v2025.0.1',
    categorias: DEMO_CATALOGO.categorias.map((c) => {
      const campos = c.campos.filter((x) => !CAMPOS_DOCUMENTO_DEMO.has(x.nome))
        .map((x) => (x.nome === 'data' ? { ...x, rotulo: 'Data de referência', dica: 'Define o ano e o mês dos fatores.' } : x));
      return {
        slug: c.slug, escopo: c.escopo, nome: c.nome, descricao: c.descricao,
        aviso: 'Modo demonstração: fatores aproximados, só para ilustrar.', campos,
        listas: [...new Set(campos.filter((x) => x.tipo.startsWith('lista:')).map((x) => x.tipo.slice(6)))],
      };
    }),
  };
}

function simularDemo(dados: Record<string, unknown>): SimResultado {
  const { cat, v, total } = calcularDemo(dados, false);
  return {
    categoria: { slug: cat.slug, nome: cat.nome, escopo: cat.escopo }, versao_ghg: 'v2025.0.1', data: String(v.data),
    emissoes: { emissao_co2: total, emissao_ch4: 0, emissao_n2o: 0, emissao_total: total, emissao_biogenica: 0 },
    detalhes: { observacao: 'Estimativa do modo demonstração (fator único por categoria)' },
    entradas: Object.fromEntries(Object.entries(v).filter(([k]) => k !== 'data')),
  };
}

export function demoApi(method: string, path: string, p: Params, body: unknown, form?: FormData): unknown {
  const ls = () => filtrar(p);
  const ano = Number(p.ano || new Date().getFullYear());
  const b = (body || {}) as Record<string, unknown>;
  switch (`${method} ${path}`) {
    case 'GET /catalogo': return DEMO_CATALOGO;
    case 'GET /simulacao/catalogo': return simuladorDemo();
    case 'GET /simulacao/listas':
      return Object.fromEntries(String(p.nomes || '').split(',').filter(Boolean).map((n) => [n, DEMO_CATALOGO.listas[n] || []]));
    case 'POST /simulacao': return simularDemo(b);
    case 'GET /resumo': return wl.resumo(filtrar(p, false), ano);
    case 'GET /mensal': return wl.mensal(ls());
    case 'GET /acumulado': return wl.mensal(ls(), true);
    case 'GET /por-categoria': return wl.porCategoria(ls());
    case 'GET /por-obra': return wl.porObra(ls());
    case 'GET /por-fornecedor': return wl.porFornecedor(ls());
    case 'GET /por-descricao': return wl.porDescricao(ls());
    case 'GET /por-material': return wl.porMaterial(ls());
    case 'GET /relatorio-ghg': return wl.relatorioGhg(filtrar(p, false), ano);
    case 'GET /lancamentos': return ls().slice(0, Number(p.limite || 50));
    case 'POST /lancamentos': return lancar(b);
    case 'GET /envios': return ENVIOS;
    case 'GET /agregado':
      return comoErroDemo(() => agregar(filtrar(p, false), {
        metrica: p.metrica, dimensao: p.dimensao, dimensao2: p.dimensao2, tipo: p.tipo, limite: p.limite,
        filtros: { escopo: p.escopo ?? undefined, categoria: p.categoria ?? undefined },
      } as Record<string, unknown>));
    case 'GET /graficos': return [...GRAFICOS_SALVOS].sort((x, y) => x.ordem - y.ordem);
    case 'POST /graficos': return comoErroDemo(() => salvarGrafico(b.titulo, b.config));
    case 'POST /envios/link': {
      const destino = obraETipo(b.id_unidade, b.tipo_documento);
      const link = String(b.link || '').trim();
      if (!/^https:\/\/[^\s/]+/.test(link)) throw new ErroDemo('Informe o link completo da pasta, começando com https://');
      return registrarEnvio({ tipo: 'link', origem: link + (b.caminho ? ` ${b.caminho}` : ''), status: 'em_processamento', mensagem: 'Previsão: até 3 dias úteis', ...camposObra(destino) });
    }
    case 'POST /envios/documentos': {
      const destino = obraETipo(form?.get('id_unidade'), form?.get('tipo_documento'));
      const arquivos = (form ? form.getAll('arquivos') : []) as File[];
      if (!arquivos.length) throw new ErroDemo('Envie pelo menos um arquivo.');
      const ruim = arquivos.find((f) => !/\.(pdf|png|jpe?g|xml)$/i.test(f.name));
      if (ruim) throw new ErroDemo(`${ruim.name}: formato não aceito. Use PDF, PNG, JPG ou XML.`);
      return registrarEnvio({
        tipo: 'documentos',
        origem: `${arquivos.length} arquivo(s): ${arquivos.slice(0, 3).map((f) => f.name).join(', ')}${arquivos.length > 3 ? '…' : ''}`,
        status: 'em_processamento', mensagem: 'Previsão: até 3 dias úteis', arquivos: arquivos.map((f) => ({ nome: f.name, tamanho: f.size })),
        ...camposObra(destino),
      });
    }
  }
  const grafico = path.match(/^\/graficos\/([\w-]+)$/);
  if (method === 'DELETE' && grafico) {
    if (!apagarGrafico(grafico[1])) throw new ErroDemo('Gráfico não encontrado.', 404);
    return { id: grafico[1] };
  }
  const escopo = path.match(/^\/por-escopo\/(\d)$/);
  if (method === 'GET' && escopo) return wl.porEscopo(filtrar(p, false), Number(escopo[1]));
  throw new ErroDemo(`Rota não simulada: ${method} ${path}`, 404);
}
